import { useState, useEffect, useCallback, useRef, type ComponentType } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { Loader2, HelpCircle, FileText, ListChecks, ListTodo } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { usePanel } from '../contexts/PanelContext';
import { generateMdx } from '../lib/markdown';
import type { SectionDocument, SectionChild } from '../lib/types';
import Tabs from '../components/Tabs';
import SectionSummaryTab, { SectionNextTab, isToDetermine, useSectionFields } from '../components/SectionSummaryTab';
import { SectionDocumentsTab } from '../components/SectionDocumentsTab';
import SectionBreadcrumbs, { type Crumb } from '../components/SectionBreadcrumbs';
import GroupStructure from '../widgets/group_structure';
import MemoryPanel from '../components/MemoryPanel';
import { useTasks } from '../components/TaskCard';
import { useSectionFigures } from '../components/FigureCard';

interface ResolvedSection {
  id: string;
  display_name: string;
  intro?: string;
  help_link?: { label: string; help_content: string };
  breadcrumbs: Crumb[];
  documents: SectionDocument[];
  subsections?: SectionChild[];
}

type Tab = 'summary' | 'next' | 'documents';

// What an area shows above its Summary, keyed by section id.
const SUMMARY_PANELS: Record<string, ComponentType[]> = {
  legal_risk: [GroupStructure],
};

export default function SectionPage() {
  const { sectionPath } = useParams<{ sectionPath: string }>();
  const location = useLocation();
  const { authFetch } = useAuth();
  const { refresh } = useWorkspace();
  const { openPanel } = usePanel();
  const [section, setSection] = useState<ResolvedSection | null>(null);
  const { fields, error: fieldsError, reload: reloadFields } = useSectionFields(sectionPath ?? '');
  const { tasks, reload: reloadTasks } = useTasks(sectionPath ?? '');
  const { figures, inputs, reload: reloadFigures } = useSectionFigures(sectionPath ?? '');
  // ?tab= opens a tab. A link to one question (?field=, from What's next or a
  // document's pending questions) opens whichever tab holds it, highlighted.
  const params = new URLSearchParams(location.search);
  const focusField = params.get('field');
  const [tab, setTab] = useState<Tab>(() => {
    const asked = params.get('tab');
    return asked === 'next' || asked === 'documents' ? asked : 'summary';
  });
  const placedFor = useRef<string | null>(params.get('tab') ? focusField : null);
  useEffect(() => {
    if (!fields || !focusField || placedFor.current === focusField) return;
    placedFor.current = focusField;
    const field = fields.find(f => f.id === focusField);
    setTab(field && isToDetermine(field) ? 'next' : 'summary');
  }, [fields, focusField]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadSection = useCallback(async () => {
    if (!sectionPath) return;
    setError(null);
    try {
      const sectionRes = await authFetch(`/workspace/section/${sectionPath}`);
      if (!sectionRes.ok) {
        setError(sectionRes.status === 404 ? 'Section not found' : 'Failed to load section');
        setSection(null);
      } else {
        setSection(await sectionRes.json());
        refresh();
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load section');
    } finally {
      setLoading(false);
    }
  }, [authFetch, sectionPath, refresh]);

  useEffect(() => { setLoading(true); loadSection(); }, [loadSection]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-brand" />
      </div>
    );
  }

  if (error || !section) {
    return <div className="text-coolslate p-6">{error || 'Failed to load section.'}</div>;
  }

  const crumbs = section.breadcrumbs ?? [{ id: section.id, display_name: section.display_name }];
  const help = section.help_link;
  const panels = SUMMARY_PANELS[section.id] ?? [];

  return (
    <div>
      <div className="pb-6 mb-6 border-b border-softgrey">
        <SectionBreadcrumbs crumbs={crumbs} />
      </div>

      {(section.intro || help) && (
        <div className="mb-6">
          {section.intro && (
            <div
              className="text-sm text-coolslate"
              dangerouslySetInnerHTML={{ __html: generateMdx(section.intro) }}
            />
          )}
          {help && (
            <button
              onClick={() => openPanel(
                help.label,
                <div className="text-sm text-coolslate leading-relaxed" dangerouslySetInnerHTML={{ __html: generateMdx(help.help_content) }} />,
              )}
              className="mt-2 inline-flex items-center gap-1.5 text-sm font-medium text-brand-dark hover:text-brand transition-colors cursor-pointer"
            >
              <HelpCircle className="w-4 h-4" />
              {help.label}
            </button>
          )}
        </div>
      )}

      <Tabs
        items={[
          { id: 'summary' as const, label: 'Summary', icon: ListChecks },
          { id: 'next' as const, label: "What's Next", icon: ListTodo },
          { id: 'documents' as const, label: 'Documents', icon: FileText },
        ]}
        active={tab}
        onChange={setTab}
      />

      {tab === 'summary' && (
        <>
          <div className="mb-6"><MemoryPanel key={section.id} scope={section.id} /></div>
          {panels.map((Panel, i) => <div key={i} className="mb-6 empty:hidden"><Panel /></div>)}
          <SectionSummaryTab
            fields={fields} error={fieldsError} onSaved={reloadFields}
            figures={figures} onFiguresSaved={reloadFigures}
          />
        </>
      )}
      {tab === 'next' && (
        <SectionNextTab
          fields={fields} error={fieldsError} onSaved={reloadFields}
          tasks={tasks} onTasksChanged={reloadTasks}
          inputs={inputs} onInputSaved={reloadFigures}
        />
      )}
      {tab === 'documents' && (
        <SectionDocumentsTab
          documents={section.documents}
          subsections={section.subsections ?? []}
          onReload={loadSection}
        />
      )}
    </div>
  );
}
